import { noteColorsOf } from "components/SmartSurveyor/shared/MarkerNoteCard";
import { LEGEND_FONT_FAMILY } from "../LegendBox";
import { LEGEND_TEXT_COLOR } from "../LegendBox/constants";
import {
  NOTE_BYLINE_OPACITY,
  NOTE_CROP_BBOX_FILL_OPACITY,
  NOTE_CROP_BBOX_STROKE,
  NOTE_CROP_BORDER_COLOR,
  NOTE_CROP_BORDER_WIDTH,
  NOTE_OUTLINE_UNDER_STROKE_COLOR,
} from "../appendNotePages/constants";
import type { NoteEntrySvgProps } from "./types";

// Puts the visual centre of a Helvetica line on the centre of its line box.
const BASELINE_CENTER_FACTOR = 0.35;

export const NoteEntrySvg = ({
  segment,
  entry,
  metrics,
  continuedLabel,
}: NoteEntrySvgProps) => {
  const colors = noteColorsOf(entry.rgb);
  const { card } = segment;
  const outlineStroke = NOTE_CROP_BBOX_STROKE * metrics.scale;
  const bodyX = card.x + metrics.frameInset;
  const bodyY = card.y + metrics.headerHeight;
  const bodyWidth = card.width - 2 * metrics.frameInset;
  const bodyHeight = card.height - metrics.headerHeight - metrics.frameInset;
  const headerX = card.x + metrics.headerPadX;
  const groupBaseline = card.y + metrics.headerPadY + metrics.groupFontSize;
  const bylineBaseline =
    groupBaseline + metrics.headerLineGap + metrics.bylineFontSize;
  const textX = bodyX + metrics.bodyPadX;
  const firstBaseline =
    bodyY +
    metrics.bodyPadY +
    metrics.lineHeight / 2 +
    metrics.bodyFontSize * BASELINE_CENTER_FACTOR;

  return (
    <g>
      {segment.kind === "head" && (
        <>
          <rect
            x={segment.crop.x}
            y={segment.crop.y}
            width={segment.crop.side}
            height={segment.crop.side}
            fill="none"
            stroke={NOTE_CROP_BORDER_COLOR}
            strokeWidth={NOTE_CROP_BORDER_WIDTH * metrics.scale}
          />
          <rect
            x={segment.outline.x1}
            y={segment.outline.y1}
            width={segment.outline.x2 - segment.outline.x1}
            height={segment.outline.y2 - segment.outline.y1}
            fill="none"
            stroke={NOTE_OUTLINE_UNDER_STROKE_COLOR}
            strokeWidth={outlineStroke * 2}
          />
          <rect
            x={segment.outline.x1}
            y={segment.outline.y1}
            width={segment.outline.x2 - segment.outline.x1}
            height={segment.outline.y2 - segment.outline.y1}
            fill={colors.frame}
            fillOpacity={NOTE_CROP_BBOX_FILL_OPACITY}
            stroke={colors.frame}
            strokeWidth={outlineStroke}
          />
        </>
      )}
      <rect
        x={card.x}
        y={card.y}
        width={card.width}
        height={card.height}
        rx={metrics.cardRadius}
        fill={colors.frame}
      />
      <text
        x={headerX}
        y={groupBaseline}
        fontFamily={LEGEND_FONT_FAMILY}
        fontSize={metrics.groupFontSize}
        fontWeight={600}
        fill={colors.ink}
      >
        {card.groupLabel}
      </text>
      <text
        x={headerX}
        y={bylineBaseline}
        fontFamily={LEGEND_FONT_FAMILY}
        fontSize={metrics.bylineFontSize}
        fill={colors.ink}
        fillOpacity={NOTE_BYLINE_OPACITY}
      >
        {card.byline}
      </text>
      {segment.kind === "continuation" && (
        <text
          x={card.x + card.width - metrics.headerPadX}
          y={groupBaseline}
          textAnchor="end"
          fontFamily={LEGEND_FONT_FAMILY}
          fontSize={metrics.bylineFontSize}
          fill={colors.ink}
          fillOpacity={NOTE_BYLINE_OPACITY}
        >
          {continuedLabel}
        </text>
      )}
      <rect
        x={bodyX}
        y={bodyY}
        width={bodyWidth}
        height={bodyHeight}
        rx={metrics.bodyRadius}
        fill="#FFFFFF"
      />
      {card.lines.map((line, index) =>
        line === "" ? null : (
          <text
            key={index}
            x={textX}
            y={firstBaseline + index * metrics.lineHeight}
            fontFamily={LEGEND_FONT_FAMILY}
            fontSize={metrics.bodyFontSize}
            fill={LEGEND_TEXT_COLOR}
          >
            {line}
          </text>
        ),
      )}
    </g>
  );
};
